import { useCallback, useEffect, useState } from 'react';
import { execApi } from '../../../services/api';
import { AdditionalItemsEditor } from './AdditionalItemsEditor';
import { TherapistDetail } from './TherapistDetail';
import { TherapistCompEditor, Rates } from '../ExecBilling/TherapistCompEditor';
import { NEW_BONUS } from '../ExecBilling/ExecBilling';
import { AuditPanel } from './AuditPanel';
import { MonthCloseBar } from './MonthCloseBar';
import { monthlyReportLink } from './reportLink';
import { ApprovalThread } from './ApprovalThread';
import { SalaryFilters, StatusFilter, BaselFilter, SentFilter, BonusFilter } from './SalaryFilters';
import { firstName } from './firstName';

// Mirrors backend app/services/exec_salary.py
interface PayLine {
  label: string; kind: string; patient_type: string; status: string;
  count: number; minutes_each: number; rate_per_meeting: number; total: number;
}
interface BonusGate { label: string; actual: number; threshold: number; passed: boolean }
interface BonusResult {
  relevant: boolean; eligible: boolean; pct: number; amount: number;
  gates: BonusGate[]; noshow_headroom: number | null;
}
interface TherapistPay {
  name: string; hebrew_name: string | null; role: string;
  available_hours: number; worked_hours: number; noshow_hours: number;
  unscheduled_hours: number; utilization: number;
  completed_meetings: number; noshow_meetings: number; scheduled_remaining: number;
  double_followups: number; irregular_slots: number;
  lines: PayLine[];
  worked_payment: number; noshow_payment: number; non_clinical_payment: number;
  unscheduled_payment: number; additional_payments: number;
  additional_items: AdditionalItem[];
  rates: TherapistRates;
  billing: BillingProfile;
  bonus: BonusResult; total_payment: number;
  manual: ManualInputs;
  report_token: string | null; sent_at: string | null; approved_at: string | null;
  // How many comments from this therapist nobody has answered yet.
  open_comments: number;
  projected_total: number | null; projected_utilization: number | null;
  // Synthetic test therapist — shown in the table, excluded from every sum.
  demo?: boolean;
  // Salaried on a payslip Lee issues; only the set bonus is paid from here.
  bonus_only?: boolean;
  // On the on-call schedule — the editor shows the on-call tariff only then.
  oncall_doctor?: boolean;
}
interface Summary {
  month: string; is_current: boolean; therapists: number;
  completed_meetings: number; noshow_meetings: number; noshow_rate: number;
  total_payment: number; projected_payment: number | null;
  double_followups: number; irregular_slots: number; unmatched_therapists: string[];
  unmatched_detail?: { name: string; completed_meetings: number;
    suggested_type: string; psy_meetings: number; cm_meetings: number }[];
}
// One therapist's row in the accounting hub: who signed off and whether it is paid.
interface AccountingState { tal: boolean; lee: boolean; paid: boolean; invoice: string }

interface ManualInputs {
  non_clinical_hours: number; non_clinical_rate: number;
  additional_payments: number; bonus_override: number | null; editable: boolean;
}
interface AdditionalItem {
  item_id: string; category: string; label: string | null;
  amount: number; hours: number | null; rate: number | null; recurring: boolean;
}
// The server sends ONE rates object (exec_salary.TherapistRates); this screen used to
// declare a subset of it, which is why passing a row to the accounting editor did not
// type-check even though the data was already right there. Reuse the editor's shape
// rather than keep a third partial copy, plus the one field it does not declare.
type TherapistRates = Rates & { noshowRate: number; overridden: string[] };
interface BillingProfile {
  employment: string; vat_registered: boolean; withholding_rate: number;
  incl_vat: number; withholding_amount: number; net_pay: number;
  invoice_type?: string;
  // Set HERE; null means the clinical system's address is what gets used.
  email?: string | null;
}
interface SalaryResp {
  summary: Summary; therapists: TherapistPay[];
  corrections_pending: { therapist: string; field: string; proposed: number; reason_held: string }[];
}

const _MON_HE = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const NOW = new Date();
const CUR_MONTH = `${NOW.getFullYear()}-${String(NOW.getMonth() + 1).padStart(2, '0')}`;
const MONTHS = _MON_HE.map((he, i) => {
  const id = `${NOW.getFullYear()}-${String(i + 1).padStart(2, '0')}`;
  return { id, label: `${he} ${NOW.getFullYear()}${id === CUR_MONTH ? ' (נוכחי)' : ''}` };
}).filter((m) => m.id <= CUR_MONTH);

const ils = (n: number) => '₪' + (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const linkFor = monthlyReportLink;
const num = (n: number, d = 0) =>
  Number(n).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });

// What each sortable column means, in one place — a header and its ordering cannot
// drift apart if they are the same key.
const SORTERS = {
  name: (t: TherapistPay) => firstName(t),   // same rule as the server's default order
  role: (t: TherapistPay) => t.role,
  done: (t: TherapistPay) => t.completed_meetings,
  remaining: (t: TherapistPay) => t.scheduled_remaining,
  noshow: (t: TherapistPay) => t.noshow_meetings,
  hours: (t: TherapistPay) => t.worked_hours,
  // Whichever bonus this person actually has — sorting by the column must order what
  // the column SHOWS, or clicking it moves the biggest case-manager bonus to the bottom.
  bonus: (t: TherapistPay) => {
    const cm = (t.additional_items ?? [])
      .filter((i) => i.category === 'cm_bonus').reduce((a, i) => a + i.amount, 0);
    if (cm) return cm;
    return t.bonus.eligible ? t.bonus.amount : -1;
  },
  total: (t: TherapistPay) => t.total_payment,
  projected: (t: TherapistPay) => t.projected_total ?? t.total_payment,
} as const;
type SortKey = keyof typeof SORTERS;
// The direction a first click should give. A money column wants the biggest at the
// top; a name column wants א׳. Same control, opposite instinct.
const TEXT_COLUMNS: SortKey[] = ['name', 'role'];

function Tile({ label, value, foot, aside }: {
  label: string; value: string; foot?: string;
  aside?: { value: string; label?: string };
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className="flex items-baseline justify-between gap-3">
        <div className="text-2xl font-semibold text-gray-900 tabular-nums tracking-tight">{value}</div>
        {aside && (
          <div className="flex items-baseline gap-1.5">
            {aside.label && <span className="text-[11px] text-gray-500">{aside.label}</span>}
            <span className="text-2xl font-semibold text-gray-900 tabular-nums tracking-tight">{aside.value}</span>
          </div>
        )}
      </div>
      {foot && <div className="text-xs text-gray-500 mt-1 tabular-nums">{foot}</div>}
    </div>
  );
}

export function ExecSalary() {
  // '' = let the SERVER pick the opening month: the last full month while it is
  // still open (payroll is worked in arrears), the current one after it closes.
  // The screen adopts whatever month the summary comes back with.
  const [month, setMonth] = useState('');
  const [data, setData] = useState<SalaryResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [role, setRole] = useState<'all' | 'psy' | 'cm'>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [baselF, setBaselF] = useState<BaselFilter>('all');
  const [sentF, setSentF] = useState<SentFilter>('all');
  const [bonusF, setBonusF] = useState<BonusFilter>('all');
  const [picked, setPicked] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Which column orders the table, and which way. Null = the server's order, which
  // is by what each therapist is owed — the right default for a payroll screen.
  const [sortBy, setSortBy] = useState<SortKey | null>(null);
  const [asc, setAsc] = useState(false);
  // Set a tariff from HERE, not only from the accounting screen. This is the screen
  // that reports "no tariff", so it is the screen that should fix it — the same
  // editor component, the viewed month, the same endpoint (Ohad, 2026-08-30). The
  // month payload already carries rates+billing per therapist, so nothing is fetched.
  // A name in "ללא תעריף" resolved to NO roster row at all — that is what unmatched
  // means (exec_salary: _resolve returned None). So the fix is not editing a tariff,
  // it is ADDING the therapist, with the name pre-filled exactly as the calendar
  // spells it — the name is the key meetings attach by, and a typo here is the same
  // silent non-payment we are trying to end.
  const [adding, setAdding] = useState<string | null>(null);
  // Edit an EXISTING therapist straight from their row (Ohad 2026-08-31), the same
  // pencil the accounting screen has. Until now the only way in from here was the
  // "ללא תעריף" strip, which is the one case that is NOT an edit — it adds someone
  // missing. Everyone else had to be found again on another screen.
  const [editing, setEditing] = useState<TherapistPay | null>(null);
  // A frozen copy of the month as it looked at a moment (Ohad 2026-08-31, same as the
  // accounting hub). Payroll recomputes from source on every load, so "what did this
  // month look like when Basel signed it off" is otherwise unanswerable.
  const [snaps, setSnaps] = useState<{ snap_id: string; label: string }[]>([]);
  const [viewingSnap, setViewingSnap] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  // (The per-therapist ledger + calc breakdown now live in the shared TherapistDetail.)

  const [exporting, setExporting] = useState(false);
  const [emailOn, setEmailOn] = useState(false);
  const [sending, setSending] = useState<string | null>(null);
  // Basel's payroll sign-off per therapist. Lives in the accounting store because
  // that is where the month's stages are kept, but it is SET from this screen only.
  const [basel, setBasel] = useState<Record<string, boolean>>({});
  //: The accounting record could not be read. The ticks below are NOT the truth.
  const [statusFailed, setStatusFailed] = useState(false);
  // What the accounting hub has done with each therapist. Read-only here — Lee and
  // Tal own these — but shown so this screen stops contradicting hers.
  const [acct, setAcct] = useState<Record<string, AccountingState>>({});
  const [approving, setApproving] = useState<string | null>(null);

  useEffect(() => {
    execApi.getSalaryEmailStatus().then((r) => setEmailOn(!!r.data.enabled)).catch(() => {});
  }, []);

  const download = useCallback(async () => {
    setExporting(true);
    try {
      const res = await execApi.getSalaryExport(month);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `תשלומים-${month}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [month]);

  const saveSnap = async () => {
    const label = new Date().toLocaleString('he-IL');
    // The rows AND Basel's sign-off flags — the sign-off is half of what makes a
    // payroll month what it was.
    const payload = JSON.stringify({ therapists: data?.therapists || [], basel, acct });
    await execApi.saveBillingSnapshot({ month, label, payload, kind: 'salary' });
    setSnaps(((await execApi.listBillingSnapshots(month, 'salary')).data as any[]) || []);
  };
  const loadSnap = async (snapId: string, label: string) => {
    const p = (await execApi.getBillingSnapshot(snapId)).data.payload;
    setData((d) => (d ? { ...d, therapists: p.therapists } : d));
    setBasel(p.basel || {});
    setAcct(p.acct || {});
    setViewingSnap(label || snapId);
  };

  const load = useCallback(async (m: string) => {
    setLoading(true); setError(null);
    try {
      // 🔴 A failure here used to be swallowed into {} — and an empty accounting
      // record is indistinguishable on screen from "nobody has approved anything".
      // On 2026-10-11 the endpoint crashed and Basel re-approved 14 therapists who
      // were already approved, because the ticks kept vanishing on every refresh.
      // The payroll table is still worth showing without it, so we keep rendering —
      // but the screen must SAY that the sign-offs could not be read, never imply
      // they are absent (Ohad, 2026-10-12).
      let statusFailed = false;
      const [res, st] = await Promise.all([
        execApi.getSalary(m),
        execApi.getBillingStatus(m).catch(() => { statusFailed = true; return { data: {} as any }; }),
      ]);
      setData(res.data);
      if (!m && res.data?.summary?.month) setMonth(res.data.summary.month);
      // The whole accounting record, not just Basel's own sign-off. This screen used
      // to keep `basel` and discard tal / lee / paid / invoice, so nothing the
      // accounting hub did was ever visible here — which is the half of "the two
      // screens don't sync" that pointed this way (Lee, 2026-09-02).
      const flags: Record<string, boolean> = {};
      const acct: Record<string, AccountingState> = {};
      Object.entries(st.data || {}).forEach(([name, v]: [string, any]) => {
        if (v?.basel) flags[name] = true;
        acct[name] = {
          tal: !!v?.tal, lee: !!v?.lee, paid: !!v?.paid, invoice: v?.invoice || '',
        };
      });
      setBasel(flags);
      setAcct(acct);
      setStatusFailed(statusFailed);
      setViewingSnap(null);
      execApi.listBillingSnapshots(m || res.data?.summary?.month, 'salary')
        .then((r) => setSnaps((r.data as any[]) || [])).catch(() => setSnaps([]));
    } catch (e: any) {
      setError(e?.response?.status === 403
        ? 'לתצוגת תשלומים נדרשת הרשאת אדמין.'
        : 'טעינת נתוני התשלומים נכשלה.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(month); }, [month, load]);

  const sendTo = useCallback(async (therapist: string) => {
    if (!emailOn) return;
    setSending(therapist);
    try {
      await execApi.sendSalary(month, therapist);
      alert('נשלח');
      load(month);
    } catch {
      alert('השליחה נכשלה');
    } finally {
      setSending(null);
    }
  }, [emailOn, month, load]);

  // Send-all mails ONLY the rows currently visible under the filter, and never
  // without an explicit "this will email N therapists" confirmation — a blast
  // to fifty freelancers is not something one stray click should do.
  const sendAll = useCallback(async (names: string[]) => {
    if (!emailOn || names.length === 0) return;
    if (!window.confirm(
      `אתה עומד לשלוח את דוח התשלום במייל ל-${names.length} מטפלים — לפי הסינון הנוכחי בטבלה.\nלהמשיך?`)) return;
    setSending('__all__');
    try {
      const r = await execApi.sendSalaryBulk(month, names);
      alert(`נשלחו ${r.data.sent}${r.data.no_email?.length ? ` · ${r.data.no_email.length} ללא מייל` : ''}`);
      load(month);
    } catch {
      alert('השליחה נכשלה');
    } finally {
      setSending(null);
    }
  }, [emailOn, month, load]);

  const approve = async (name: string, next: boolean) => {
    setApproving(name);
    try {
      await execApi.baselApprove(month, name, next);
      setBasel((b) => ({ ...b, [name]: next }));
    } catch {
      alert('שמירת האישור נכשלה');
    } finally {
      setApproving(null);
    }
  };

  if (loading) return <div className="text-gray-500 text-sm">טוען…</div>;
  if (error) return <div className="text-red-600 text-sm">{error}</div>;
  if (!data) return null;

  // Role filter drives the tiles too — a filter that only trims the table
  // leaves the headline numbers describing a different population.
  const byRole = data.therapists.filter((t) => role === 'all' || t.role === role);
  const statusOf = (t: TherapistPay): StatusFilter =>
    t.open_comments > 0 ? 'commented' : t.approved_at ? 'approved' : 'pending';
  const statusCounts = {
    all: byRole.length,
    approved: byRole.filter((t) => statusOf(t) === 'approved').length,
    pending: byRole.filter((t) => statusOf(t) === 'pending').length,
    commented: byRole.filter((t) => statusOf(t) === 'commented').length,
  } as Record<StatusFilter, number>;
  const bonusOf = (t: TherapistPay): BonusFilter =>
    !t.bonus.relevant ? 'na' : t.bonus.eligible ? 'eligible' : 'not';
  const baselCounts = {
    all: byRole.length,
    yes: byRole.filter((t) => basel[t.name]).length,
    no: byRole.filter((t) => !basel[t.name]).length,
  } as Record<BaselFilter, number>;
  const sentCounts = {
    all: byRole.length,
    sent: byRole.filter((t) => t.sent_at).length,
    unsent: byRole.filter((t) => !t.sent_at).length,
  } as Record<SentFilter, number>;
  const bonusCounts = {
    all: byRole.length,
    eligible: byRole.filter((t) => bonusOf(t) === 'eligible').length,
    not: byRole.filter((t) => bonusOf(t) === 'not').length,
    na: byRole.filter((t) => bonusOf(t) === 'na').length,
  } as Record<BonusFilter, number>;

  const therapists = byRole
    .filter((t) => status === 'all' || statusOf(t) === status)
    .filter((t) => baselF === 'all' || (baselF === 'yes') === !!basel[t.name])
    .filter((t) => sentF === 'all' || (sentF === 'sent') === !!t.sent_at)
    .filter((t) => bonusF === 'all' || bonusOf(t) === bonusF)
    .filter((t) => picked.length === 0 || picked.includes(t.name));
  const sorted = sortBy ? [...therapists].sort((a, b) => {
    const va = SORTERS[sortBy](a);
    const vb = SORTERS[sortBy](b);
    const cmp = typeof va === 'string'
      ? String(va).localeCompare(String(vb), 'he')
      : Number(va) - Number(vb);
    return asc ? cmp : -cmp;
  }) : therapists;
  // Header tiles recompute from the visible rows — minus demo rows, whose money
  // is fake by construction (the server summary excludes them the same way).
  const real = therapists.filter((t) => !t.demo);
  const sum = (f: (t: TherapistPay) => number) => real.reduce((a, t) => a + f(t), 0);
  const done = sum((t) => t.completed_meetings);
  const noshow = sum((t) => t.noshow_meetings);
  const s = {
    ...data.summary,
    therapists: real.length,
    completed_meetings: done,
    noshow_meetings: noshow,
    noshow_rate: done + noshow ? Math.round((noshow / (done + noshow)) * 1000) / 10 : 0,
    total_payment: sum((t) => t.total_payment),
    projected_payment: sum((t) => t.projected_total ?? t.total_payment),
    noshow_payment: sum((t) => t.noshow_payment),
    double_followups: sum((t) => t.double_followups),
    irregular_slots: sum((t) => t.irregular_slots),
  };

  // A header that orders the table, in THREE states: biggest-first, smallest-first,
  // then off. The third one matters — the table arrives in the server's order, by what
  // each therapist is owed, and with only asc/desc that order becomes unreachable: a
  // second click on "מטפל" gives א׳→ת׳, not the way the screen opened (Ohad, 2026-07-30).
  const Th = ({ k, children }: { k: SortKey; children: React.ReactNode }) => {
    const on = sortBy === k;
    const cycle = () => {
      const firstAsc = TEXT_COLUMNS.includes(k);        // names א׳→ת׳, numbers high→low
      if (!on) { setSortBy(k); setAsc(firstAsc); }      // 1st
      else if (asc === firstAsc) setAsc(!firstAsc);     // 2nd: reverse
      else { setSortBy(null); setAsc(false); }         // 3rd: back to the original
      setOpen(null);
    };
    return (
      <th className="text-right font-medium px-3 py-2">
        <button
          onClick={cycle}
          title={on
            ? (asc === TEXT_COLUMNS.includes(k) ? 'לחיצה נוספת — סדר הפוך' : 'לחיצה נוספת — חזרה לסדר המקורי')
            : ''}
          className={`inline-flex items-center gap-1 hover:text-gray-800 ${
            on ? 'text-blue-700 font-semibold' : ''}`}
        >
          {children}
          <span className="text-[10px]">{on ? (asc ? '▲' : '▼') : '⇅'}</span>
        </button>
      </th>
    );
  };

  return (
    <div dir="rtl" className="space-y-5">
      {statusFailed && (
        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          🔴 <b>לא הצלחנו לקרוא את מצב האישורים לחודש הזה.</b> סימוני האישור בטבלה
          למטה אינם אמינים כרגע — ייתכן שמטפלים שכבר אושרו מוצגים כלא-מאושרים.
          <b> אל תאשרו מחדש</b> לפני רענון מוצלח.
          <button type="button" onClick={() => load(month)}
            className="mr-2 underline font-semibold">נסו שוב</button>
        </div>
      )}
      <MonthCloseBar month={month} onClosed={() => load(month)} />

      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={month}
          onChange={(e) => { setMonth(e.target.value); setOpen(null); }}
          className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white"
        >
          {MONTHS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
          {([
            ['all', 'הכל', data.therapists.length],
            ['psy', 'רופאים', data.therapists.filter((t) => t.role === 'psy').length],
            ['cm', 'מנהלי תיק', data.therapists.filter((t) => t.role === 'cm').length],
          ] as const).map(([id, label, count]) => (
            <button
              key={id}
              onClick={() => { setRole(id); setOpen(null); }}
              className={`text-sm px-3 py-1.5 ${
                role === id ? 'bg-blue-600 text-white font-medium' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {label} <span className={role === id ? 'text-blue-100' : 'text-gray-500'}>{count}</span>
            </button>
          ))}
        </div>
        <SalaryFilters
          status={status} setStatus={(v) => { setStatus(v); setOpen(null); }}
          counts={statusCounts}
          basel={baselF} setBasel={(v) => { setBaselF(v); setOpen(null); }}
          baselCounts={baselCounts}
          sent={sentF} setSent={(v) => { setSentF(v); setOpen(null); }}
          sentCounts={sentCounts}
          bonus={bonusF} setBonus={(v) => { setBonusF(v); setOpen(null); }}
          bonusCounts={bonusCounts}
          names={byRole.map((t) => ({ name: t.name, label: t.hebrew_name || t.name }))}
          picked={picked} setPicked={(p) => { setPicked(p); setOpen(null); }}
          open={pickerOpen} setOpen={setPickerOpen}
        />
        <button
          onClick={() => sendAll(therapists.filter((t) => !t.demo).map((t) => t.name))}
          disabled={!emailOn || sending !== null}
          title={emailOn ? 'שולח לכל המטפלים המוצגים לפי הסינון הנוכחי' : 'שליחת הדואר אינה מוגדרת'}
          className="text-sm rounded-md px-3 py-1.5 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed bg-blue-600 text-white hover:bg-blue-700"
        >
          {sending === '__all__' ? 'שולח…' : 'שלח לכולם'}
        </button>
        <button
          onClick={download}
          disabled={exporting}
          className="text-sm border border-gray-300 bg-white hover:bg-gray-50 disabled:text-gray-400 rounded-md px-3 py-1.5"
        >
          {exporting ? 'מכין…' : '⤓ דוח סגירת חודש'}
        </button>
        {/* Accounting works the same month in parallel and neither screen hears about
            the other's saves, so both need a way to pull the current state without
            reloading the browser (Lee, 2026-09-02). */}
        <button
          onClick={() => load(month)}
          disabled={loading || !!viewingSnap}
          title="טעינה מחדש מהשרת — מציג שינויים שנעשו בהנהלת חשבונות"
          className="text-sm border border-gray-300 bg-white hover:bg-gray-50 disabled:text-gray-400 rounded-md px-3 py-1.5"
        >
          ↻ רענון
        </button>
        <button
          onClick={saveSnap}
          disabled={!data || !!viewingSnap}
          title="שמירת עותק קפוא של החודש כפי שהוא נראה עכשיו"
          className="text-sm border border-gray-300 bg-white hover:bg-gray-50 disabled:text-gray-400 rounded-md px-3 py-1.5"
        >
          📸 snapshot
        </button>
        {snaps.length > 0 && (
          <select
            value=""
            onChange={(e) => {
              const sn = snaps.find((x) => x.snap_id === e.target.value);
              if (sn) loadSnap(sn.snap_id, sn.label);
            }}
            className="text-sm border border-gray-300 bg-white rounded-md px-2 py-1.5 text-gray-600"
          >
            <option value="">📸 {snaps.length} שמורים…</option>
            {snaps.map((sn) => <option key={sn.snap_id} value={sn.snap_id}>{sn.label}</option>)}
          </select>
        )}
        {/* Manual copy-all — a fallback for when mail is not configured. Demoted to a
            quiet text link, and hidden entirely once email works. */}
        {!emailOn && (
          <button
            onClick={() => {
              const text = therapists
                .filter((t) => t.report_token)
                .map((t) => `${t.hebrew_name || t.name}\t${ils(t.total_payment)}\t${linkFor(t.report_token!)}`)
                .join('\n');
              navigator.clipboard.writeText(text);
              setCopied('__all__');
              setTimeout(() => setCopied((c) => (c === '__all__' ? null : c)), 2500);
            }}
            className="text-xs text-gray-500 hover:text-gray-600 underline"
          >
            {copied === '__all__' ? '✓ הועתקו' : 'העתק קישורים ידנית'}
          </button>
        )}
        {/* Was a one-line amber chip of bare names here until 2026-10-06. A name alone
            is easy to walk past; these are doctors whose entire month is missing from
            every total on the screen, so it now says how many meetings and what the
            calendar says they are. */}
      </div>

      {adding && (
        <TherapistCompEditor
          isNew
          month={month}
          suggestedType={(s.unmatched_detail || []).find((u) => u.name === adding)?.suggested_type}
          therapist={{
            name: adding, hebrew_name: '', archived: false,
            rates: {
              workedRate: 0, nonClinicalRate: 0, oncallRate: 1500,
              tariffIntakeAdult: 0, tariffIntakeYouth: 0,
              tariffFollowupAdult: 0, tariffFollowupYouth: 0,
              intakeLengthAdults: 0, intakeLengthYouth: 0,
              followupLengthAdults: 0, followupLengthYouth: 0,
              noshowPct: 0, ...NEW_BONUS,
            },
            billing: { employment: 'freelancer', vat_registered: true, withholding_rate: 0 },
          }}
          onClose={() => setAdding(null)}
          onSaved={() => { setAdding(null); load(month); }}
        />
      )}

      {editing && (
        <TherapistCompEditor
          month={month}
          therapist={{
            name: editing.name, hebrew_name: editing.hebrew_name, role: editing.role,
            rates: editing.rates, billing: editing.billing,
            oncall_doctor: editing.oncall_doctor,
            total_payment: editing.total_payment,
            completed_meetings: editing.completed_meetings,
            noshow_meetings: editing.noshow_meetings,
          }}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(month); }}
        />
      )}

      {/* Above the tiles, because the tiles are wrong while this is non-empty: every
          meeting these doctors held is missing from the numbers under them. */}
      {(s.unmatched_detail || []).length > 0 && (
        <div className="rounded-lg bg-red-50 border border-red-300 px-4 py-3 text-sm">
          <div className="text-red-900 font-medium">
            🔴 {(s.unmatched_detail || []).length} רופאים עם פגישות ואינם ברשימת השכר ·{' '}
            {(s.unmatched_detail || []).reduce((a, u) => a + u.completed_meetings, 0)} פגישות —
            הפגישות שלהם אינן נספרות באף מספר במסך הזה
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(s.unmatched_detail || []).map((u) => (
              <button key={u.name} onClick={() => setAdding(u.name)}
                title={`הוספה לרשימת השכר · לפי היומן: ${
                  u.suggested_type === 'Psychiatrist' ? 'פסיכיאטר' : 'מנהל תיק'} (${
                  u.psy_meetings} פסיכיאטר, ${u.cm_meetings} מנהל תיק)`}
                className="text-xs rounded-full border border-red-400 bg-white px-2.5 py-1 text-red-900 hover:bg-red-100">
                {u.name} · {u.completed_meetings} פגישות
                {u.suggested_type && (
                  <span className="text-red-500">
                    {' '}· {u.suggested_type === 'Psychiatrist' ? 'פסיכיאטר' : 'מנהל תיק'}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <AuditPanel month={month} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label={s.is_current ? 'שולם עד כה' : 'עלות תשלומים'} value={ils(s.total_payment)}
              foot={`${num(s.completed_meetings)} פגישות בוצעו`} />
        {s.is_current && (
          <Tile label="צפי לסוף החודש" value={ils(s.projected_payment ?? 0)} foot="אם כל המתוזמנות יתקיימו" />
        )}
        <Tile label="שיעור no-show" value={`${s.noshow_rate}%`}
              aside={{ value: ils(s.noshow_payment), label: 'שולם' }}
              foot={`${num(s.noshow_meetings)} פגישות`} />
        <Tile label="מעקבים כפולים" value={num(s.double_followups)}
              foot={s.irregular_slots ? `${s.irregular_slots} סלוטים חריגים` : 'אין סלוטים חריגים'} />
      </div>

      {/* While a snapshot is on screen the numbers are historical. Saying so loudly
          matters more here than in accounting: this table has approve and send
          buttons next to every row. */}
      {viewingSnap && (
        <div className="flex items-center gap-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
          📸 מציג snapshot מ-{viewingSnap} — המספרים היסטוריים ואינם מתעדכנים
          <button onClick={() => load(month)} className="mr-auto text-amber-700 underline">
            חזרה לנתונים חיים
          </button>
        </div>
      )}

      {data.corrections_pending.length > 0 && (
        <div className="text-xs text-gray-500">
          ממתין להכרעת באסל:{' '}
          {data.corrections_pending.map((c) => `${c.therapist} — ${c.field} → ${c.proposed} דק׳`).join(' · ')}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 border-b border-gray-200">
              <th className="text-right font-medium px-3 py-2 w-8"></th>
              <th className="text-center font-medium px-3 py-2 w-8"></th>
              <Th k="name">מטפל</Th>
              <Th k="role">תפקיד</Th>
              <Th k="done">בוצעו</Th>
              {s.is_current && <Th k="remaining">מתוזמנות</Th>}
              <Th k="noshow">no-show</Th>
              <Th k="hours">שעות עבודה</Th>
              <Th k="bonus">בונוס</Th>
              <Th k="total">{s.is_current ? 'שולם עד כה' : 'סה״כ תשלום'}</Th>
              {s.is_current && <Th k="projected">צפי</Th>}
              <th className="text-right font-medium px-3 py-2">אישור באסל</th>
              <th className="text-right font-medium px-3 py-2">נשלח</th>
              <th className="text-right font-medium px-3 py-2">אושר</th>
              <th className="text-right font-medium px-3 py-2">פעולות</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((t) => {
              const isOpen = open === t.name;
              return (
                <>
                  <tr
                    key={t.name}
                    onClick={() => setOpen(isOpen ? null : t.name)}
                    className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${isOpen ? 'bg-blue-50/40' : ''}`}
                  >
                    <td className="px-3 py-2 text-gray-500">{isOpen ? '▾' : '▸'}</td>
                    {/* Its own column, matching the accounting table. stopPropagation
                        because the row opens the pay breakdown and the pencil opens the
                        config — two different questions about the same person. */}
                    <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                      <button type="button" title="עריכת תעריפים, פרופיל ומייל"
                        onClick={() => setEditing(t)}
                        className="text-gray-500 hover:text-blue-600">✎</button>
                    </td>
                    <td className="px-3 py-2 font-medium text-gray-900">
                      {t.hebrew_name || t.name}
                      {/* The calendar key, kept visible — see the note in ExecBilling. */}
                      {t.hebrew_name && t.hebrew_name !== t.name && (
                        <div dir="ltr" className="text-[11px] font-normal text-gray-500 text-right">{t.name}</div>
                      )}
                      {t.demo && <span className="mr-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">דמה</span>}
                      {/* The meeting counts stay on the row but none of their money does,
                          so the row has to say so — otherwise 52 meetings beside ₪240
                          reads as an underpayment. */}
                      {t.bonus_only && (
                        <span
                          className="mr-1.5 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700"
                          title="מקבל/ת שכר בתלוש. כאן משולם בונוס הסטים בלבד — הפגישות כבר משולמות בתלוש."
                        >בונוס בלבד</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-xs text-gray-500 border border-gray-200 rounded-full px-2 py-0.5">
                        {t.role === 'psy' ? 'פסיכיאטר' : 'CM'}
                      </span>
                    </td>
                    <td className="px-3 py-2 tabular-nums">{num(t.completed_meetings)}</td>
                    {s.is_current && <td className="px-3 py-2 tabular-nums text-gray-500">{num(t.scheduled_remaining)}</td>}
                    <td className="px-3 py-2 tabular-nums">{num(t.noshow_meetings)}</td>
                    <td className="px-3 py-2 tabular-nums">
                      {num(t.worked_hours, 2)}
                      {(t.unscheduled_hours > 0 || t.manual.non_clinical_hours > 0) && (
                        <span className="text-gray-500 text-xs"
                          title={`שעות בתשלום שאינן פגישות: ${num(t.unscheduled_hours, 2)} לא-מתוזמנות · ${num(t.manual.non_clinical_hours, 2)} לא-קליניות`}>
                          {' '}+{num(t.unscheduled_hours + t.manual.non_clinical_hours, 2)}
                        </span>
                      )}
                    </td>
                    {/* One bonus column for two different bonuses. A psychiatrist's is
                        the gated percentage; a case manager's is the set bonus, which
                        arrives as a computed additional item (cm_bonus). Before this the
                        column simply read "—" for every case manager, so 28 people with a
                        real bonus looked like they had none (Ohad, 2026-08-31). */}
                    <td className="px-3 py-2 tabular-nums">
                      {(() => {
                        const cm = (t.additional_items ?? [])
                          .filter((i) => i.category === 'cm_bonus');
                        if (cm.length) {
                          const amt = cm.reduce((a, i) => a + i.amount, 0);
                          return (
                            <span className="text-green-700 font-medium"
                              title={cm.map((i) => i.label).filter(Boolean).join(' · ') || 'בונוס סטים'}>
                              {ils(amt)}
                            </span>
                          );
                        }
                        if (!t.bonus.relevant) return <span className="text-gray-500">—</span>;
                        return t.bonus.eligible
                          ? <span className="text-green-700 font-medium">{ils(t.bonus.amount)}</span>
                          : <span className="text-gray-500">אין</span>;
                      })()}
                    </td>
                    <td className="px-3 py-2 tabular-nums font-semibold">{ils(t.total_payment)}</td>
                    {s.is_current && <td className="px-3 py-2 tabular-nums">{ils(t.projected_total ?? t.total_payment)}</td>}
                    {/* The first gate of the month. Basel signs off the figure here;
                        accounting sees it and cannot set it (Ohad, 2026-07-30). */}
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      {basel[t.name] ? (
                        <button
                          onClick={() => approve(t.name, false)}
                          disabled={approving === t.name}
                          title="בטל אישור"
                          className="text-xs font-medium text-green-700 hover:text-green-900 whitespace-nowrap"
                        >
                          ✓ אושר
                        </button>
                      ) : (
                        <button
                          onClick={() => approve(t.name, true)}
                          disabled={approving === t.name}
                          className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 whitespace-nowrap disabled:text-gray-300"
                        >
                          {approving === t.name ? '…' : 'אשר'}
                        </button>
                      )}
                      {/* What accounting has done since. Read-only: Lee and Tal own
                          these, and the backend refuses to set them from here — but
                          hiding them made this screen contradict hers. */}
                      {(() => {
                        const a = acct[t.name];
                        if (!a || (!a.tal && !a.lee && !a.paid && !a.invoice)) return null;
                        const marks = [a.tal && 'לילך', a.lee && 'לי', a.paid && 'שולם']
                          .filter(Boolean).join(' · ');
                        return (
                          <div className="mt-1 text-[10px] text-gray-500 whitespace-nowrap"
                               title={a.invoice ? `חשבונית ${a.invoice}` : 'הנהלת חשבונות'}>
                            {marks && <span className="text-gray-500">{marks}</span>}
                            {a.invoice && <span className="mr-1">🧾 {a.invoice}</span>}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-gray-500 text-xs">{t.sent_at ?? '—'}</td>
                    {/* An objection is louder than a blank: a therapist who wrote back
                        instead of approving must be visible from the table, not only
                        to whoever happens to open the card (Ohad, 2026-07-30). */}
                    <td className="px-3 py-2 text-gray-500 text-xs">
                      {t.open_comments > 0 ? (
                        <span className="text-amber-700 font-medium whitespace-nowrap"
                              title="הערות שטרם נסגרו — פתח/י את הכרטיס">
                          💬 {t.open_comments > 1 ? `${t.open_comments} הערות` : 'הערה'}
                        </span>
                      ) : t.approved_at ? (
                        <span className="text-green-700 font-semibold whitespace-nowrap" title={t.approved_at}>
                          ✓ אושר {t.approved_at.slice(0, 10)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-3 items-center whitespace-nowrap">
                        {/* The therapist's own report. It was labelled "תצוגה", which
                            says nothing about what opens (Ohad, 2026-07-28) — it is a
                            button now, named for the thing it shows. */}
                        {t.report_token ? (
                          <a href={linkFor(t.report_token)} target="_blank" rel="noreferrer"
                             className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700">
                            ↗ הדוח החודשי
                          </a>
                        ) : (
                          <span className="rounded-md bg-gray-100 px-2.5 py-1 text-xs text-gray-500"
                                title="אין קישור לדוח למטפל הזה">הדוח החודשי</span>
                        )}
                        <button
                          onClick={() => { if (t.report_token) { navigator.clipboard.writeText(linkFor(t.report_token)); setCopied(t.name); setTimeout(() => setCopied((c) => (c === t.name ? null : c)), 1800); } }}
                          className="text-xs text-blue-700 hover:text-blue-900"
                        >{copied === t.name ? '✓ הועתק' : 'העתק קישור'}</button>
                        <button
                          onClick={() => sendTo(t.name)}
                          disabled={!emailOn || sending !== null}
                          title={emailOn ? '' : 'שליחת הדואר אינה מוגדרת'}
                          className="text-xs whitespace-nowrap text-blue-700 hover:text-blue-900 disabled:text-gray-300 disabled:cursor-not-allowed"
                        >
                          {sending === t.name ? 'שולח…' : (t.sent_at ? 'שלח שוב' : 'שלח')}
                        </button>
                      </div>
                    </td>
                  </tr>

                  {isOpen && (
                    <tr key={`${t.name}-detail`} className="bg-gray-50/70 border-b border-gray-200">
                      <td colSpan={s.is_current ? 15 : 13} className="px-6 py-4">
                        {/* Voucher on the right where it has always been, the exchange
                            on the left where there was room. The thread lives here and
                            not inside TherapistDetail because that component is shared
                            with the accounting hub, which has no approval flow. */}
                        <div className="flex flex-col lg:flex-row gap-4 items-stretch">
                          <div className="flex-1 min-w-0">
                            <TherapistDetail therapist={t} month={month} isCurrent={s.is_current} />
                          </div>
                          <div className="lg:w-80 shrink-0">
                            <ApprovalThread
                              month={month}
                              therapist={t.name}
                              count={t.open_comments}
                              onResolved={() => load(month)}
                            />
                          </div>
                        </div>

                        {/* Additional payments — categorized line-items (incl. non-clinical
                            hours), recurring or one-off. Comp CONFIG (rates/profile) moved
                            to the accounting hub (Ohad 2026-07-23); this stays for the
                            per-month hours entry. */}
                        <AdditionalItemsEditor
                          therapist={t}
                          month={month}
                          nonClinicalRate={t.manual.non_clinical_rate}
                          onSaved={() => load(month)}
                        />
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>


      <p className="text-xs text-gray-500">
        התשלום מחושב לפי פגישה: כל פגישה משלמת את התעריף שלה, בלי קשר לאורך הסלוט ביומן.
        אורך הסלוט משמש רק לזיהוי <b>מעקב כפול</b> (בדיוק פי 2), שמשולם כפול.
        שעות זמינות נשלפות חי מ־<code>therapist_load</code>.
      </p>
    </div>
  );
}
